import React, { useState, useEffect, useRef, useMemo } from 'react';
import './ObservationModal.css';
import useNormesTemplates from '../../hooks/useNormesTemplates';
import useArretesTemplates from '../../hooks/useArretesTemplates';
import { buildArreteEtape14Link } from '../../services/arretesService';
import apiService from '../../services/apiService';
import inspectionStorage from '../../services/inspectionStorage';
import { loadEtape2ReferentielSelection } from '../../services/etape2ReferentielService';

export default function ObservationModal({
  isOpen,
  onClose,
  onSave,
  context,
  observation: editingObservation,
  initialPhotos = [],
  missionId = null,
}) {
  // === Onglets source de l'observation : Norme ou Arrêté ===
  const [sourceType, setSourceType] = useState('norme');

  // === Restriction des normes affichées selon l'étape 2 du rapport ===
  // - etape2NormeIdSet : set des norme_id cochés dans etape_2 (null = pas de restriction)
  // - etape2ArreteIdSet : set des arrete_id (etape2_key) cochés dans etape_2
  // - showAllNormes : toggle utilisateur pour bypasser la restriction
  const [etape2NormeIdSet, setEtape2NormeIdSet] = useState(null);
  const [etape2ArreteIdSet, setEtape2ArreteIdSet] = useState(null);
  const [showAllNormes, setShowAllNormes] = useState(false);

  useEffect(() => {
    if (!isOpen || !missionId) {
      setEtape2NormeIdSet(null);
      setEtape2ArreteIdSet(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [etape2Data, mapping] = await Promise.all([
          inspectionStorage.getStep(missionId, 'etape_2_normes'),
          loadEtape2ReferentielSelection(),
        ]);
        if (cancelled) return;
        const checkedNormes = etape2Data?.normes || {};
        const ids = new Set();
        if (Array.isArray(mapping?.normes)) {
          for (const row of mapping.normes) {
            if (row.norme_id != null && checkedNormes[row.etape2_key]) {
              ids.add(String(row.norme_id));
            }
          }
        }
        setEtape2NormeIdSet(ids.size > 0 ? ids : null);

        const checkedArretes = etape2Data?.arretes || {};
        const arreteIds = new Set();
        for (const [key, val] of Object.entries(checkedArretes)) {
          if (val) arreteIds.add(key);
        }
        setEtape2ArreteIdSet(arreteIds.size > 0 ? arreteIds : null);
      } catch (e) {
        console.warn('[ObservationModal] chargement filtre etape 2 échoué:', e?.message || e);
        if (!cancelled) {
          setEtape2NormeIdSet(null);
          setEtape2ArreteIdSet(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, missionId]);

  const effectiveAllowedIds = useMemo(() => {
    if (showAllNormes) return null;
    if (!etape2NormeIdSet || etape2NormeIdSet.size === 0) return null;
    return etape2NormeIdSet;
  }, [showAllNormes, etape2NormeIdSet]);

  const {
    loading,
    error,
    normes,
    paragraphes,
    categories,
    templates,
    selectedDomaine,
    selectedNormeId,
    selectedParagraphe,
    selectedCategorie,
    selectedTemplate,
    normeInfo,
    handleDomaineChange,
    handleNormeChange,
    handleParagrapheChange,
    handleCategorieChange,
    handleTemplateSelect,
    resetFilters
  } = useNormesTemplates({ allowedNormeIds: effectiveAllowedIds });

  // === Cascade Arrêtés ===
  const allowedArreteIdsForHook = useMemo(() => {
    if (!etape2ArreteIdSet || etape2ArreteIdSet.size === 0) return null;
    return etape2ArreteIdSet;
  }, [etape2ArreteIdSet]);

  const {
    loading: arretesLoading,
    error: arretesError,
    arretesDisponibles,
    articles: arreteArticles,
    templates: arreteTemplates,
    selectedArreteCode,
    selectedArticleId: selectedArreteArticleId,
    selectedTemplate: selectedArreteTemplate,
    handleArreteChange,
    handleArticleChange: handleArreteArticleChange,
    handleTemplateSelect: handleArreteTemplateSelect,
    resetFilters: resetArreteFilters,
  } = useArretesTemplates({ allowedArreteIds: allowedArreteIdsForHook });

  const selectedArreteArticle = useMemo(
    () => arreteArticles.find((a) => a.id === selectedArreteArticleId) || null,
    [arreteArticles, selectedArreteArticleId]
  );

  const selectedArreteInfo = useMemo(
    () => arretesDisponibles.find((a) => a.code === selectedArreteCode) || null,
    [arretesDisponibles, selectedArreteCode]
  );

  const [formData, setFormData] = useState({
    localisation: '',
    element: '',
    circuit: '',
    constat: '',
    reference_reglementaire: '',
    element_ref: null
  });

  const [photos, setPhotos] = useState([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // ✅ Initialisation du formulaire (mode création ou édition)
  useEffect(() => {
    if (isOpen) {
      if (editingObservation) {
        // Mode édition : pré-remplir avec l'observation existante
        setFormData({
          localisation: editingObservation.localisation || '',
          element: editingObservation.element || '',
          circuit: editingObservation.circuit || '',
          constat: editingObservation.constat || '',
          reference_reglementaire: editingObservation.reference_reglementaire || '',
          element_ref: editingObservation.element_ref || null
        });
        setPhotos(editingObservation.photos || []);
      } else if (context) {
        // Mode création avec contexte
        setFormData({
          localisation: context.localisation || '',
          element: context.element || '',
          circuit: context.circuit || '',
          constat: '',
          reference_reglementaire: '',
          element_ref: context.element_ref || null
        });
        setPhotos(Array.isArray(initialPhotos) ? initialPhotos : []);
      } else {
        // Mode création sans contexte
        setFormData({
          localisation: '',
          element: '',
          circuit: '',
          constat: '',
          reference_reglementaire: '',
          element_ref: null
        });
        setPhotos(Array.isArray(initialPhotos) ? initialPhotos : []);
      }
      setUploadError('');
    }
  }, [isOpen, context, editingObservation, initialPhotos]);

  // ✅ Application du template Norme sélectionné
  useEffect(() => {
    if (selectedTemplate) {
      setFormData(prev => ({
        ...prev,
        constat: selectedTemplate.constat || '',
        reference_reglementaire: selectedTemplate.reference_reglementaire || ''
      }));
    }
  }, [selectedTemplate]);

  // ✅ Application du template Arrêté sélectionné
  useEffect(() => {
    if (selectedArreteTemplate) {
      const ref =
        selectedArreteTemplate.reference_reglementaire
        || (selectedArreteArticle
          ? `Arrêté du ${selectedArreteCode} - Art. ${selectedArreteArticle.article_numero}`
          : '');
      setFormData(prev => ({
        ...prev,
        constat: selectedArreteTemplate.constat || '',
        reference_reglementaire: ref,
      }));
    }
  }, [selectedArreteTemplate, selectedArreteArticle, selectedArreteCode]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fallback : convertit un File en dataURL base64 si l'upload S3 échoue,
  // pour que la photo soit conservée localement et ré-uploadée à la prochaine
  // sync (via syncService.uploadPendingObservationPhotos).
  const fileToDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('FileReader error'));
      reader.readAsDataURL(file);
    });

  const handlePhotoFiles = async (files) => {
    if (!files || files.length === 0) return;
    setIsUploadingPhoto(true);
    setUploadError('');
    const newEntries = [];
    let fallbackCount = 0;
    for (const file of Array.from(files)) {
      let result = null;
      try {
        result = await apiService.uploadFile(file, 'photo_observation');
      } catch (err) {
        console.warn('[ObservationModal] uploadFile a throw:', err?.message || err);
      }
      if (result?.success && result?.file_url) {
        newEntries.push(result.file_url);
      } else {
        // Fallback : on garde la photo en base64 dans le state. Elle sera ré-uploadée
        // à la prochaine sync. C'est mieux que de la perdre.
        try {
          const dataUrl = await fileToDataURL(file);
          if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
            newEntries.push(dataUrl);
            fallbackCount += 1;
          } else {
            setUploadError(`Erreur upload : ${result?.error || 'conversion impossible'}`);
          }
        } catch (err) {
          setUploadError(`Erreur upload : ${result?.error || err?.message || 'inconnue'}`);
        }
      }
    }
    if (newEntries.length > 0) {
      setPhotos(prev => [...prev, ...newEntries]);
      if (fallbackCount > 0) {
        setUploadError(`⚠️ ${fallbackCount} photo(s) conservée(s) localement (upload retenté à la prochaine sync)`);
      }
    }
    setIsUploadingPhoto(false);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  const handleRemovePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.localisation || !formData.element || !formData.constat) {
      alert('⚠️ Veuillez remplir les champs obligatoires (Localisation, Élément, Constat)');
      return;
    }

    // Détermine si un template (norme OU arrêté) a été utilisé
    const usedNormeTemplate = sourceType === 'norme' && selectedTemplate && normeInfo;
    const usedArreteTemplate = sourceType === 'arrete' && selectedArreteTemplate && selectedArreteInfo;

    // ✅ CORRECTION : Construire l'observation SANS le numéro
    // C'est Etape11 qui attribuera le numéro basé sur les données locales
    const observation = {
      // Si édition, conserver le numéro existant
      ...(editingObservation && { numero: editingObservation.numero }),

      localisation: formData.localisation,
      element: formData.element,
      circuit: formData.circuit || '',
      constat: formData.constat,
      reference_reglementaire: formData.reference_reglementaire || '',
      custom: !usedNormeTemplate && !usedArreteTemplate,
      photos: photos,
      element_ref: formData.element_ref,
      created_date: editingObservation?.created_date || new Date().toISOString(),
      source_type: usedArreteTemplate ? 'arrete' : 'norme',

      // Données du template Norme si sélectionné
      ...(usedNormeTemplate && {
        template_id: selectedTemplate.id,
        template_code: selectedTemplate.code,
        norme_code: normeInfo.code,
        norme_titre: normeInfo.titre,
        domaine: normeInfo.domaine,
        paragraphe_code: selectedTemplate.paragraphe_code,
        paragraphe_titre: selectedTemplate.paragraphe_titre,
        categorie_code: selectedTemplate.categorie_code,
        categorie_titre: selectedTemplate.categorie_titre,
        lien_etape_14: selectedTemplate.lien_etape_14 || ''
      }),

      // Données du template Arrêté si sélectionné
      ...(usedArreteTemplate && {
        template_arrete_id: selectedArreteTemplate.id,
        arrete_code: selectedArreteCode,
        arrete_titre: selectedArreteInfo.titre,
        arrete_description: selectedArreteInfo.description,
        article_arrete_id: selectedArreteArticle?.id,
        article_numero: selectedArreteArticle?.article_numero,
        article_prescription: selectedArreteArticle?.prescription,
        lien_etape_14:
          selectedArreteTemplate.lien_etape_14
          || buildArreteEtape14Link(selectedArreteCode, selectedArreteArticle?.article_numero),
      }),
    };

    onSave(observation);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      localisation: '',
      element: '',
      circuit: '',
      constat: '',
      reference_reglementaire: '',
      element_ref: null
    });
    setPhotos([]);
    setUploadError('');
    setSourceType('norme');
    resetFilters();
    resetArreteFilters();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="obs-modal-overlay" onClick={onClose}>
      <div className="obs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="obs-modal-header">
          <div>
            <h2 className="obs-modal-title">
              {editingObservation ? '✏️ Modifier l\'observation' : '➕ Créer une observation'}
            </h2>
            <p className="obs-modal-subtitle">
              Sélectionnez un template depuis le référentiel ou créez une observation personnalisée
            </p>
          </div>
          <button onClick={onClose} className="obs-modal-close">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="obs-modal-form">
          <div className="obs-modal-content">
            
            {/* Section 1 : Sélection depuis le référentiel */}
            <div className="obs-modal-section">
              <h3 className="obs-modal-section-title">
                <span className="obs-modal-section-number">1</span>
                Sélection du template
              </h3>

              {/* Onglets Source : Norme / Arrêté */}
              <div className="obs-modal-source-tabs" role="tablist" aria-label="Source de l'observation">
                <button
                  type="button"
                  role="tab"
                  aria-selected={sourceType === 'norme'}
                  className={`obs-modal-source-tab ${sourceType === 'norme' ? 'active' : ''}`}
                  onClick={() => setSourceType('norme')}
                >
                  📘 Par Norme
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={sourceType === 'arrete'}
                  className={`obs-modal-source-tab ${sourceType === 'arrete' ? 'active' : ''}`}
                  onClick={() => setSourceType('arrete')}
                  disabled={arretesDisponibles.length === 0}
                  title={arretesDisponibles.length === 0 ? 'Aucun arrêté coché à l\'étape 2' : ''}
                >
                  📜 Par Arrêté
                  {arretesDisponibles.length === 0 && ' (aucun)'}
                </button>
              </div>

              {/* === Volet NORME === */}
              {sourceType === 'norme' && loading && (
                <div className="obs-modal-loading">
                  <div className="obs-modal-spinner"></div>
                  <p className="obs-modal-loading-text">Chargement du référentiel...</p>
                </div>
              )}

              {sourceType === 'norme' && error && (
                <div className="obs-modal-error">
                  <div className="obs-modal-error-title">❌ Erreur</div>
                  <p>{error}</p>
                </div>
              )}

              {sourceType === 'norme' && !loading && !error && (
                <div className="obs-modal-cascade">
                  
                  {/* Étape 0 : Domaine (BT/HT) */}
                  <div className="obs-modal-field has-value">
                    <label className="obs-modal-label">
                      ⚡ Domaine de tension
                    </label>
                    <div className="obs-modal-domaine-toggle" role="tablist" aria-label="Domaine de tension">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={selectedDomaine === 'BT'}
                        className={`obs-modal-domaine-btn ${selectedDomaine === 'BT' ? 'active' : ''}`}
                        onClick={() => handleDomaineChange('BT')}
                      >
                        BT — Basse tension
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={selectedDomaine === 'HT'}
                        className={`obs-modal-domaine-btn ${selectedDomaine === 'HT' ? 'active' : ''}`}
                        onClick={() => handleDomaineChange('HT')}
                      >
                        HT — Haute tension
                      </button>
                    </div>
                  </div>

                  {/* Filtre étape 2 (info + toggle) */}
                  {etape2NormeIdSet && etape2NormeIdSet.size > 0 && (
                    <div className="obs-modal-etape2-banner">
                      <div className="obs-modal-etape2-info">
                        <span className="obs-modal-etape2-icon">🎯</span>
                        <span className="obs-modal-etape2-text">
                          {showAllNormes
                            ? `Toutes les normes affichées (${etape2NormeIdSet.size} cochées en étape 2)`
                            : `Seules les ${etape2NormeIdSet.size} norme(s) cochées en étape 2 sont affichées`}
                        </span>
                      </div>
                      <label className="obs-modal-etape2-toggle">
                        <input
                          type="checkbox"
                          checked={showAllNormes}
                          onChange={(e) => setShowAllNormes(e.target.checked)}
                        />
                        <span>Tout afficher</span>
                      </label>
                    </div>
                  )}

                  {/* Étape 1 : Norme */}
                  <div className={`obs-modal-field ${selectedNormeId ? 'has-value' : ''}`}>
                    <label className="obs-modal-label">
                      📋 1. Sélectionnez la norme ({selectedDomaine})
                      {selectedNormeId && <span className="obs-modal-count-badge">{paragraphes.length} parag.</span>}
                    </label>
                    <select
                      className="obs-modal-select"
                      value={selectedNormeId}
                      onChange={(e) => handleNormeChange(e.target.value)}
                    >
                      <option value="">{normes.length === 0 ? `Aucune norme ${selectedDomaine}` : '-- Choisir une norme --'}</option>
                      {normes && normes.length > 0 && normes.map(norme => (
                        <option key={norme.id} value={norme.id}>
                          {norme.code} - {norme.titre} ({norme.domaine})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Étape 2 : Paragraphe */}
                  {selectedNormeId && (
                    <div className={`obs-modal-field ${selectedParagraphe ? 'has-value' : ''}`}>
                      <label className="obs-modal-label">
                        📖 2. Sélectionnez le paragraphe
                        {selectedParagraphe && <span className="obs-modal-count-badge">{categories.length} cat.</span>}
                      </label>
                      <select
                        className="obs-modal-select"
                        value={selectedParagraphe}
                        onChange={(e) => handleParagrapheChange(e.target.value)}
                      >
                        <option value="">-- Choisir un paragraphe --</option>
                        {paragraphes.map(para => (
                          <option key={para.code} value={para.code}>
                            § {para.code} - {para.titre}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Étape 3 : Catégorie */}
                  {selectedParagraphe && (
                    <div className={`obs-modal-field ${selectedCategorie ? 'has-value' : ''}`}>
                      <label className="obs-modal-label">
                        🏷️ 3. Sélectionnez la catégorie
                        {selectedCategorie && <span className="obs-modal-count-badge">{templates.length} obs.</span>}
                      </label>
                      <select
                        className="obs-modal-select"
                        value={selectedCategorie}
                        onChange={(e) => handleCategorieChange(e.target.value)}
                      >
                        <option value="">-- Choisir une catégorie --</option>
                        {categories.map(cat => (
                          <option key={cat.code} value={cat.code}>
                            {cat.code} - {cat.titre}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Étape 4 : Template */}
                  {selectedCategorie && templates.length > 0 && (
                    <div className="obs-modal-field">
                      <label className="obs-modal-label">
                        ✅ 4. Choisissez un template d'observation
                      </label>
                      <div className="obs-modal-template-list">
                        {templates.map(template => (
                          <div
                            key={template.id}
                            className={`obs-modal-template-item ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                            onClick={() => handleTemplateSelect(template)}
                          >
                            <div className="obs-modal-template-title">
                              {template.constat}
                            </div>
                            <div className="obs-modal-template-ref">
                              {template.reference_reglementaire}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedCategorie && templates.length === 0 && (
                    <div className="obs-modal-empty">
                      <div className="obs-modal-empty-icon">📭</div>
                      <div className="obs-modal-empty-text">
                        Aucun template disponible pour cette catégorie
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* === Volet ARRETE === */}
              {sourceType === 'arrete' && arretesLoading && (
                <div className="obs-modal-loading">
                  <div className="obs-modal-spinner"></div>
                  <p className="obs-modal-loading-text">Chargement des arrêtés...</p>
                </div>
              )}

              {sourceType === 'arrete' && arretesError && (
                <div className="obs-modal-error">
                  <div className="obs-modal-error-title">❌ Erreur</div>
                  <p>{arretesError}</p>
                </div>
              )}

              {sourceType === 'arrete' && !arretesLoading && !arretesError && (
                <div className="obs-modal-cascade">

                  {arretesDisponibles.length === 0 && (
                    <div className="obs-modal-empty">
                      <div className="obs-modal-empty-icon">📜</div>
                      <div className="obs-modal-empty-text">
                        Aucun arrêté coché à l'étape 2 du rapport
                      </div>
                    </div>
                  )}

                  {arretesDisponibles.length > 0 && etape2ArreteIdSet && (
                    <div className="obs-modal-etape2-banner">
                      <div className="obs-modal-etape2-info">
                        <span className="obs-modal-etape2-icon">🎯</span>
                        <span className="obs-modal-etape2-text">
                          {arretesDisponibles.length} arrêté(s) coché(s) à l'étape 2
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Étape 1 : Arrêté */}
                  {arretesDisponibles.length > 0 && (
                    <div className={`obs-modal-field ${selectedArreteCode ? 'has-value' : ''}`}>
                      <label className="obs-modal-label">
                        📜 1. Sélectionnez l'arrêté
                        {selectedArreteCode && <span className="obs-modal-count-badge">{arreteArticles.length} art.</span>}
                      </label>
                      <select
                        className="obs-modal-select"
                        value={selectedArreteCode}
                        onChange={(e) => handleArreteChange(e.target.value)}
                      >
                        <option value="">-- Choisir un arrêté --</option>
                        {arretesDisponibles.map(ar => (
                          <option key={ar.id} value={ar.code}>
                            {ar.titre} — {ar.description}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Étape 2 : Article */}
                  {selectedArreteCode && arreteArticles.length > 0 && (
                    <div className={`obs-modal-field ${selectedArreteArticleId ? 'has-value' : ''}`}>
                      <label className="obs-modal-label">
                        📄 2. Sélectionnez l'article
                        {selectedArreteArticleId && <span className="obs-modal-count-badge">{arreteTemplates.length} obs.</span>}
                      </label>
                      <select
                        className="obs-modal-select"
                        value={selectedArreteArticleId}
                        onChange={(e) => handleArreteArticleChange(e.target.value)}
                      >
                        <option value="">-- Choisir un article --</option>
                        {arreteArticles.map(art => (
                          <option key={art.id} value={art.id}>
                            Art. {art.article_numero} — {art.prescription}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {selectedArreteCode && arreteArticles.length === 0 && (
                    <div className="obs-modal-empty">
                      <div className="obs-modal-empty-icon">📭</div>
                      <div className="obs-modal-empty-text">
                        Aucun article disponible pour cet arrêté
                      </div>
                    </div>
                  )}

                  {/* Étape 3 : Template */}
                  {selectedArreteArticleId && arreteTemplates.length > 0 && (
                    <div className="obs-modal-field">
                      <label className="obs-modal-label">
                        ✅ 3. Choisissez un template d'observation
                      </label>
                      <div className="obs-modal-template-list">
                        {arreteTemplates.map(template => (
                          <div
                            key={template.id}
                            className={`obs-modal-template-item ${selectedArreteTemplate?.id === template.id ? 'selected' : ''}`}
                            onClick={() => handleArreteTemplateSelect(template)}
                          >
                            <div className="obs-modal-template-title">
                              {template.constat}
                            </div>
                            <div className="obs-modal-template-ref">
                              {template.reference_reglementaire
                                || `Arrêté du ${selectedArreteCode} - Art. ${selectedArreteArticle?.article_numero || ''}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedArreteArticleId && arreteTemplates.length === 0 && (
                    <div className="obs-modal-empty">
                      <div className="obs-modal-empty-icon">📭</div>
                      <div className="obs-modal-empty-text">
                        Aucun template disponible pour cet article
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Section 2 : Détails de l'observation */}
            <div className="obs-modal-section">
              <h3 className="obs-modal-section-title">
                <span className="obs-modal-section-number">2</span>
                Détails de l'observation
              </h3>

              <div className="obs-modal-row">
                <div className="obs-modal-field">
                  <label className="obs-modal-label">
                    📍 Localisation <span className="obs-modal-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.localisation}
                    onChange={(e) => handleChange('localisation', e.target.value)}
                    placeholder="Ex: Atelier, Bureau, RESERVE..."
                    className="obs-modal-input"
                    required
                  />
                </div>

                <div className="obs-modal-field">
                  <label className="obs-modal-label">
                    🔧 Élément concerné <span className="obs-modal-required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.element}
                    onChange={(e) => handleChange('element', e.target.value)}
                    placeholder="Ex: TGBT, Prise P1, Local..."
                    className="obs-modal-input"
                    required
                  />
                </div>
              </div>

              <div className="obs-modal-field">
                <label className="obs-modal-label">⚡ Circuit</label>
                <input
                  type="text"
                  value={formData.circuit}
                  onChange={(e) => handleChange('circuit', e.target.value)}
                  placeholder="Ex: E1, PC2..."
                  className="obs-modal-input"
                />
              </div>

              <div className="obs-modal-field">
                <label className="obs-modal-label">
                  📝 Constat / Préconisation <span className="obs-modal-required">*</span>
                </label>
                <textarea
                  value={formData.constat}
                  onChange={(e) => handleChange('constat', e.target.value)}
                  rows={6}
                  placeholder="Décrivez le constat et les préconisations..."
                  className="obs-modal-textarea"
                  required
                />
              </div>

              <div className="obs-modal-field">
                <label className="obs-modal-label">📚 Référence réglementaire</label>
                <input
                  type="text"
                  value={formData.reference_reglementaire}
                  onChange={(e) => handleChange('reference_reglementaire', e.target.value)}
                  placeholder="Ex: Art. R4215-9 - NF C15-100 § 521"
                  className="obs-modal-input obs-modal-input-mono"
                />
              </div>
            </div>

            {/* Section 3 : Photos */}
            <div className="obs-modal-section obs-modal-section-photos">
              <h3 className="obs-modal-section-title">
                <span className="obs-modal-section-number">3</span>
                Photos <span className="obs-modal-optional">(optionnel)</span>
              </h3>

              {/* Miniatures */}
              {photos.length > 0 && (
                <div className="obs-modal-photo-grid">
                  {photos.map((url, idx) => (
                    <div key={idx} className="obs-modal-photo-item">
                      <img
                        src={url}
                        alt={`Photo ${idx + 1}`}
                        className="obs-modal-photo-thumb"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(idx)}
                        className="obs-modal-photo-remove"
                        title="Supprimer cette photo"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Inputs fichier cachés */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={e => handlePhotoFiles(e.target.files)}
              />
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => handlePhotoFiles(e.target.files)}
              />

              {/* Boutons */}
              <div className="obs-modal-photo-actions">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                  className="obs-modal-photo-btn obs-modal-photo-btn-camera"
                >
                  {isUploadingPhoto ? '⏳ Envoi...' : '📷 Prendre une photo'}
                </button>
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                  className="obs-modal-photo-btn obs-modal-photo-btn-gallery"
                >
                  🖼️ Importer depuis la galerie
                </button>
              </div>

              {uploadError && (
                <p className="obs-modal-photo-error">❌ {uploadError}</p>
              )}

              <p className="obs-modal-photo-hint">
                Sur tablette : "Prendre une photo" ouvre directement l'appareil photo arrière. Plusieurs photos peuvent être ajoutées par observation.
              </p>
            </div>

          </div>

          {/* Footer avec boutons */}
          <div className="obs-modal-footer">
            <button type="button" onClick={resetForm} className="obs-modal-btn-cancel">
              Annuler
            </button>
            <button 
              type="submit" 
              className="obs-modal-btn-save"
              disabled={!formData.localisation || !formData.element || !formData.constat}
            >
              {editingObservation ? '✅ Modifier' : '✅ Créer l\'observation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}